// Pure call-activity owner routing, extracted from App.tsx `saveCallReview` so
// the branchy id-prefix logic can be unit-tested in isolation. When the dialer
// is opened from a record it prefixes the contact id by entity (`deal-123`,
// `lead-456`, `contact-ABC`, `client-ABC`); this maps that id back to the
// (owner_type_id, owner_id) | unified_client_id the notes/activity API expects.
// Behaviour is identical to the original inline block — do not "improve" it
// without updating callRouting.test.ts.

export interface CallActivityOwner {
  /** 3 = project, 2 = deal, 1 = lead, 0 = not owner-typed (route via unifiedClientId). */
  ownerTypeId: number
  /** Numeric for deal/lead; the TEXT project id (verbatim string) for projects. */
  ownerId: number | string
  /** Set for contact-/client- ids, which route via unified_client_id instead. */
  unifiedClientId: string | undefined
}

export function resolveCallActivityOwner(rawId: string): CallActivityOwner {
  const id = String(rawId)
  let ownerTypeId = 0
  let ownerId: number | string = 0
  let unifiedClientId: string | undefined = undefined
  if (id.startsWith('deal-')) {
    ownerTypeId = 2
    ownerId = Number(id.slice(5).replace(/\D/g, '')) || 0
  } else if (id.startsWith('lead-')) {
    ownerTypeId = 1
    ownerId = Number(id.slice(5).replace(/\D/g, '')) || 0
  } else if (id.startsWith('project-')) {
    // Projects use a TEXT id (owner_type_id = 3, owner_id = the project id as a
    // string). Verbatim slice — NO digit-stripping, unlike deal-/lead- ids.
    // A bare `project-` yields ownerId '' (an owner_type_id-3 contact-only edge,
    // per isProjectOwnedRow in src/lib/calls/dealRollup.ts).
    ownerTypeId = 3
    ownerId = id.slice('project-'.length)
  } else if (id.startsWith('contact-') || id.startsWith('client-')) {
    // Contacts/clients don't live in owner_type_id/owner_id — they go via
    // `unified_client_id` instead, which the notes API handles specially.
    unifiedClientId = id.replace(/^(?:contact|client)-/, '')
  } else if (/^\d+$/.test(id)) {
    // Bare numeric id — legacy path; assume lead (best-guess).
    ownerTypeId = 1
    ownerId = Number(id)
  }
  return { ownerTypeId, ownerId, unifiedClientId }
}
